import type { StatusResult } from '@/api';
import type { PendingAction } from '@/types';

interface Props {
  status: StatusResult | null;
  pending: PendingAction;
}

const STATE_LABEL: Record<string, string> = {
  pending: 'Starting',
  running: 'Running',
  stopping: 'Stopping',
  'shutting-down': 'Shutting down',
  stopped: 'Stopped',
  terminated: 'Terminated',
  unknown: 'Unknown',
};

const STATE_COLOR: Record<string, string> = {
  running: 'bg-green-100 text-green-800',
  stopped: 'bg-slate-100 text-slate-800',
  stopping: 'bg-orange-100 text-orange-800',
  pending: 'bg-amber-100 text-amber-800',
  'shutting-down': 'bg-orange-100 text-orange-800',
  terminated: 'bg-red-100 text-red-800',
  unknown: 'bg-slate-100 text-slate-600',
};

export default function InstanceCard({ status, pending }: Props) {
  const state = (pending ? (pending === 'start' ? 'pending' : 'stopping') : (status?.state ?? 'unknown')) as string;
  const label = STATE_LABEL[state] ?? 'Unknown';
  const color = STATE_COLOR[state] ?? STATE_COLOR.unknown;
  const instanceId = status?.instanceId ?? '—';
  const region = status?.region ?? '—';
  const lastUpdated = status?.lastUpdated;

  return (
    <div className="rounded-xl bg-white p-6 shadow">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-medium text-slate-900">Minecraft Server</h2>
          <p className="text-sm text-slate-500">Instance {instanceId} · {region}</p>
        </div>
        <span className={`state-badge ${color}`}>{label}</span>
      </div>
      {pending && (
        <p className="mt-3 text-sm text-slate-600">
          Waiting for the instance to reach the {pending === 'start' ? 'running' : 'stopped'} state…
        </p>
      )}
      {status?.error && !pending && (
        <p className="mt-3 text-sm text-rose-600">{status.error}</p>
      )}
      {lastUpdated && !pending && (
        <p className="mt-3 text-xs text-slate-400">Last updated {new Date(lastUpdated).toLocaleString()}</p>
      )}
    </div>
  );
}
